import { formatDate, formatDateTime } from '@sentinel/web-core';
import { StateBadge, type StateTone } from '@sentinel/ui';
import { useTranslation } from 'react-i18next';
import type { Notification } from '@sentinel/shared';

const K = 'shell.notifications';
/** Levels L0–L3 on the resource states' tones: the word says the level, the glyph shows it. */
const TONE: Record<number, StateTone> = { 0: 'info', 1: 'att', 2: 'att', 3: 'crit' };
/** The date a kind speaks of, in UTC ISO 8601: shown in the reader's time zone as `date`. */
const DATE_PARAMS = ['expiresAt', 'dueAt', 'startedAt'] as const;

/** The text of a kind, with its values formatted for the reader (dates in their time zone). */
function useNotificationText(n: Notification): string {
  const { t } = useTranslation();
  const { qualification } = n.params;
  const when = DATE_PARAMS.map((key) => n.params[key]).find((v) => typeof v === 'string');
  return t(`${K}.kinds.${n.kind}`, {
    ...n.params,
    qualification: t(`personnel.qualifications.kinds.${String(qualification)}`, {
      defaultValue: String(qualification),
    }),
    date: formatDate(typeof when === 'string' ? when : null),
  });
}

interface Props {
  notification: Notification;
  onOpen: (notification: Notification) => void;
}

/** One row of the list: level, text, time; unread rows in bold. */
export function NotificationItem({ notification, onOpen }: Props) {
  const { t } = useTranslation();
  const text = useNotificationText(notification);
  return (
    <button
      type="button"
      className="ntf-item"
      role="menuitem"
      data-unread={notification.readAt === null}
      onClick={() => onOpen(notification)}
    >
      <span>{text}</span>
      <span className="mt">
        <StateBadge
          tone={TONE[notification.level] ?? 'info'}
          label={t(`${K}.levels.${String(notification.level)}`)}
        />
        {formatDateTime(notification.createdAt)}
      </span>
    </button>
  );
}
